import { DEBOUNCE_MS } from '@constants/timing';
import { LOCATION_FILTERS as F } from '@locales/id/locations';
import { regionPaths, regionSearch, useApi, type District, type OffsetPage, type Village, type VillageDetail } from '@lib/api';
import { useDebounced } from '@lib/hooks';

/** Every typed word is in the name ("pasar minggu", "ragunan"). */
const matches = (name: string, q: string) => {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return words.every((w) => name.toLowerCase().includes(w));
};

/** What a district or village list hands its picker. */
export interface AreaListState<T> {
  state: { data: unknown; loading: boolean; error: string | null; retry: () => void };
  items: T[];
  partial: boolean;
}

/**
 * One parent's districts or villages, searchable, never all of them. The first page covers almost every parent whole
 * (a regency's districts, a district's villages): while it does, the typed words narrow it here, at once. A parent
 * with more than a page is searched by the server from REGION_LIST.searchMin letters; until then `partial` says the
 * list is cut (the picker asks to type). `firstPath` null: nothing is fetched (the level above is not chosen yet).
 */
function useAreaList<T extends { code: string; name: string }>(firstPath: string | null, searchPath: (q: string) => string, q: string): AreaListState<T> {
  const query = useDebounced(q.trim(), DEBOUNCE_MS.pickerSearch);
  const first = useApi<OffsetPage<T>>(firstPath);
  const big = !!first.data && first.data.meta.total > first.data.items.length;
  const remoteQ = big ? regionSearch(query) : undefined;
  // no keepPrevious: an earlier search's names never stand in for this one's while it loads
  const remote = useApi<OffsetPage<T>>(firstPath && remoteQ ? searchPath(remoteQ) : null);
  const typed = q.trim();
  const items = remoteQ ? (remote.data?.items ?? []) : (first.data?.items ?? []).filter((x) => !typed || matches(x.name, typed));
  return {
    /** the request behind what is shown (its loading, error and retry) */
    state: remoteQ ? remote : first,
    items,
    /** the list is cut: type REGION_LIST.searchMin letters or more to search all of it */
    partial: big && !remoteQ,
  };
}

/** A regency's districts (kecamatan); `active` false fetches nothing (a closed menu). */
export function useDistricts(regencyCode: string, q: string, active = true) {
  return useAreaList<District>(active && regencyCode ? regionPaths.districts({ regencyCode }) : null, (query) => regionPaths.districts({ regencyCode, q: query }), q);
}

/** A district's villages (desa / kelurahan); `active` false fetches nothing. */
export function useVillages(districtCode: string, q: string, active = true) {
  return useAreaList<Village>(active && districtCode ? regionPaths.villages({ districtCode }) : null, (query) => regionPaths.villages({ districtCode, q: query }), q);
}

/**
 * The names of a chosen district and village for chips and labels (a link carries only the codes): a village's
 * detail names its district too (one request); a district alone is found in its regency's first page. While a name
 * loads (or when it cannot be found) the chip says what was chosen ("Kecamatan terpilih"), never a raw code.
 */
export function useAreaNames(regencyCode: string, districtCode: string, villageCode: string) {
  const village = useApi<VillageDetail>(villageCode ? regionPaths.village(villageCode) : null);
  const districts = useApi<OffsetPage<District>>(districtCode && !villageCode && regencyCode ? regionPaths.districts({ regencyCode }) : null);
  const district = village.data?.districtName ?? districts.data?.items.find((d) => d.code === districtCode)?.name;
  return {
    districtName: districtCode ? (district ?? F.districtChosen) : null,
    villageName: villageCode ? (village.data?.name ?? F.villageChosen) : null,
  };
}
